import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Badge,
  Input,
  Button,
  Spinner,
  Collapse,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { waybillsAPI, projectsAPI } from '../../helpers/api';
import { useIsMobile } from '../../helpers/useMediaQuery';
import WaybillDetailModal from './WaybillDetailModal';
import SideModal from '../../components/Common/SideModal';
import AuthImage, { openAuthFile } from '../../components/Common/AuthImage';

interface WaybillPhoto {
  id: string;
  fileName: string;
  filePath: string;
  createdAt: string;
}

interface Waybill {
  id: string;
  waybillNumber: string;
  qrCode: string | null;
  status: string;
  supplierName: string | null;
  notes: string | null;
  createdAt: string;
  scannedAt: string | null;
  gibMatchedAt: string | null;
  site: { name: string; project: { name: string } } | null;
  scannedBy: { firstName: string; lastName: string } | null;
  createdBy: { firstName: string; lastName: string } | null;
  quantity: number | null;
  unit: string | null;
  tonnage: number | null;
  photos?: WaybillPhoto[];
}

const statusColors: Record<string, string> = {
  SCANNED: 'info',
  PENDING: 'warning',
  MATCHED: 'success',
  NOT_ARRIVED: 'danger',
  MANUAL: 'secondary',
};

const statusLabels: Record<string, string> = {
  SCANNED: 'Okutuldu',
  PENDING: 'Bekliyor',
  MATCHED: 'Eşleşti',
  NOT_ARRIVED: 'Gelmedi',
  MANUAL: 'Manuel',
};

interface Site {
  id: string;
  name: string;
  project: { name: string };
}

interface LogoWaybill {
  ficheno: string;
  docode: string;
  date: string;
  trcode: number;
  clientName: string | null;
  waybillNumber: string;
  isMatched: boolean;
}

interface LogoWaybillLine {
  itemCode: string;
  itemName: string;
  amount: number;
  price: number;
  total: number;
  unitName: string | null;
}

const WaybillList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get('status');
  const isMobile = useIsMobile();

  const [waybills, setWaybills] = useState<Waybill[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWaybill, setSelectedWaybill] = useState<Waybill | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [matching, setMatching] = useState(false);

  // Filter states
  const [statusFilterState, setStatusFilterState] = useState(statusFilter || '');
  const [siteFilter, setSiteFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Sites for filter dropdown
  const [sites, setSites] = useState<Site[]>([]);

  // Logo waybills
  const [logoWaybills, setLogoWaybills] = useState<LogoWaybill[]>([]);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoLoadingMore, setLogoLoadingMore] = useState(false);
  const [logoSearch, setLogoSearch] = useState('');
  const [logoOffset, setLogoOffset] = useState(0);
  const [logoHasMore, setLogoHasMore] = useState(true);
  const [showLogoSection, setShowLogoSection] = useState(true);
  const [logoStatusFilter, setLogoStatusFilter] = useState<'all' | 'matched' | 'pending'>('all');
  const [selectedLogoWaybill, setSelectedLogoWaybill] = useState<LogoWaybill | null>(null);
  const [showLogoDetailModal, setShowLogoDetailModal] = useState(false);
  const [logoDetailLoading, setLogoDetailLoading] = useState(false);
  const [matchedSystemWaybill, setMatchedSystemWaybill] = useState<Waybill | null>(null);
  const [logoLines, setLogoLines] = useState<LogoWaybillLine[]>([]);
  const [logoLinesLoading, setLogoLinesLoading] = useState(false);
  const LOGO_PAGE_SIZE = 50;

  useEffect(() => {
    fetchSites();
    fetchLogoWaybills();
  }, []);

  useEffect(() => {
    fetchWaybills();
  }, [statusFilterState, siteFilter, startDate, endDate]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchWaybills();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchFilter]);

  const fetchSites = async () => {
    try {
      const response = await projectsAPI.getSites();
      setSites(response.data);
    } catch (error) {
      console.error('Sites fetch error:', error);
    }
  };

  const handleMatchAll = async () => {
    try {
      setMatching(true);
      const response = await waybillsAPI.logoMatchAll();
      const { total, matched } = response.data;

      if (matched > 0) {
        toast.success(`${matched}/${total} irsaliye Logo ile eşleştirildi`);
        fetchWaybills();
        fetchLogoWaybills(logoSearch); // Refresh Logo list to remove matched items
      } else if (total === 0) {
        toast.info('Eşleştirilecek irsaliye bulunamadı');
      } else {
        toast.warning(`${total} irsaliye kontrol edildi, eşleşme bulunamadı`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Eşleştirme başarısız');
    } finally {
      setMatching(false);
    }
  };

  const fetchWaybills = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilterState) params.status = statusFilterState;
      if (siteFilter) params.siteId = siteFilter;
      if (searchFilter) params.search = searchFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await waybillsAPI.getAll(params);
      setWaybills(response.data);
    } catch (error) {
      console.error('Waybills fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogoWaybills = async (search?: string, append = false) => {
    try {
      if (append) {
        setLogoLoadingMore(true);
      } else {
        setLogoLoading(true);
        setLogoOffset(0);
      }

      const currentOffset = append ? logoOffset : 0;
      const params: any = { limit: LOGO_PAGE_SIZE + 1 }; // +1 to check if there are more
      if (search) params.search = search;

      const response = await waybillsAPI.logoList(params);
      const data = response.data;

      // Check if there are more items
      const hasMore = data.length > LOGO_PAGE_SIZE;
      const items = hasMore ? data.slice(0, LOGO_PAGE_SIZE) : data;

      if (append) {
        setLogoWaybills((prev) => [...prev, ...items]);
        setLogoOffset(currentOffset + LOGO_PAGE_SIZE);
      } else {
        setLogoWaybills(items);
        setLogoOffset(LOGO_PAGE_SIZE);
      }
      setLogoHasMore(hasMore);
    } catch (error) {
      console.error('Logo waybills fetch error:', error);
    } finally {
      setLogoLoading(false);
      setLogoLoadingMore(false);
    }
  };

  const loadMoreLogoWaybills = () => {
    fetchLogoWaybills(logoSearch, true);
  };

  // Debounced Logo search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLogoWaybills(logoSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [logoSearch]);

  const clearFilters = () => {
    setStatusFilterState('');
    setSiteFilter('');
    setSearchFilter('');
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters = statusFilterState || siteFilter || searchFilter || startDate || endDate;

  const openDetail = (waybill: Waybill) => {
    setSelectedWaybill(waybill);
    setShowDetailModal(true);
  };

  const openLogoDetail = async (logoWaybill: LogoWaybill) => {
    setSelectedLogoWaybill(logoWaybill);
    setMatchedSystemWaybill(null);
    setLogoLines([]);
    setShowLogoDetailModal(true);

    // Malzeme satırlarını getir
    try {
      setLogoLinesLoading(true);
      const linesRes = await waybillsAPI.logoLines(logoWaybill.ficheno);
      setLogoLines(linesRes.data);
    } catch (error) {
      console.error('Logo lines fetch error:', error);
    } finally {
      setLogoLinesLoading(false);
    }

    // Eşleşmişse sistem irsaliyesini getir
    if (logoWaybill.isMatched) {
      try {
        setLogoDetailLoading(true);
        const response = await waybillsAPI.getAll({ search: logoWaybill.waybillNumber });
        const found = response.data.find((w: Waybill) => w.waybillNumber === logoWaybill.waybillNumber);
        if (found) {
          setMatchedSystemWaybill(found);
        }
      } catch (error) {
        console.error('System waybill fetch error:', error);
      } finally {
        setLogoDetailLoading(false);
      }
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MOBILE VIEW
  // ═══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <>
        <Row>
          <Col xs={12}>
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0">İrsaliyeler</h4>
              <div className="page-title-right">
                <Link to="/waybills/scan" className="btn btn-primary">
                  <i className="ri-add-line me-1"></i>
                  İrsaliye Ekle
                </Link>
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                {/* Search */}
                <InputGroup size="sm" className="mb-3">
                  <InputGroupText>
                    <i className="ri-search-line"></i>
                  </InputGroupText>
                  <Input
                    type="text"
                    placeholder="İrsaliye no veya tedarikçi ara..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </InputGroup>

                {/* Filter Toggle */}
                <button
                  className="btn btn-sm btn-light w-100 mb-3 d-flex align-items-center justify-content-between"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <span>
                    <i className="ri-filter-3-line me-2"></i>
                    Filtreler
                    {hasActiveFilters && <Badge color="primary" className="ms-2">{[statusFilterState, siteFilter, startDate, endDate].filter(Boolean).length}</Badge>}
                  </span>
                  <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line`}></i>
                </button>

                {/* Collapsible Filters */}
                <Collapse isOpen={showFilters}>
                  <div className="bg-light p-3 rounded mb-3">
                    <div className="mb-2">
                      <label className="small text-muted mb-1">Durum</label>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={statusFilterState}
                        onChange={(e) => setStatusFilterState(e.target.value)}
                      >
                        <option value="">Tüm Durumlar</option>
                        <option value="SCANNED">Okutuldu</option>
                        <option value="PENDING">Bekliyor</option>
                        <option value="NOT_ARRIVED">Gelmedi</option>
                        <option value="MANUAL">Manuel</option>
                      </Input>
                    </div>

                    <div className="mb-2">
                      <label className="small text-muted mb-1">Şantiye</label>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                      >
                        <option value="">Tüm Şantiyeler</option>
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.project.name} / {site.name}
                          </option>
                        ))}
                      </Input>
                    </div>

                    <Row>
                      <Col xs={6}>
                        <div className="mb-2">
                          <label className="small text-muted mb-1">Başlangıç</label>
                          <Input
                            type="date"
                            bsSize="sm"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="mb-2">
                          <label className="small text-muted mb-1">Bitiş</label>
                          <Input
                            type="date"
                            bsSize="sm"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                          />
                        </div>
                      </Col>
                    </Row>

                    {hasActiveFilters && (
                      <button
                        className="btn btn-sm btn-outline-secondary w-100 mt-2"
                        onClick={clearFilters}
                      >
                        <i className="ri-close-line me-1"></i>
                        Filtreleri Temizle
                      </button>
                    )}
                  </div>
                </Collapse>

                {/* List */}
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                  </div>
                ) : waybills.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">Henüz irsaliye kaydı bulunmuyor.</p>
                    <Link to="/waybills/scan" className="btn btn-primary">
                      İrsaliye Ekle
                    </Link>
                  </div>
                ) : (
                  <div className="admin-card-list">
                    {waybills
                      .filter((w) => w.status !== 'MATCHED')
                      .map((waybill) => (
                      <div key={waybill.id} className="admin-card-item">
                        <div
                          className="admin-card-header"
                          onClick={() => openDetail(waybill)}
                          style={{ cursor: 'pointer' }}
                        >
                          <h6 className="admin-card-title">{waybill.waybillNumber}</h6>
                          <Badge color={statusColors[waybill.status]}>
                            {statusLabels[waybill.status]}
                          </Badge>
                        </div>
                        <div
                          className="admin-card-body"
                          onClick={() => openDetail(waybill)}
                          style={{ cursor: 'pointer' }}
                        >
                          {waybill.supplierName && (
                            <div className="admin-info-row">
                              <span className="admin-info-label">
                                <i className="ri-store-2-line"></i>
                                Tedarikçi
                              </span>
                              <span className="admin-info-value">{waybill.supplierName}</span>
                            </div>
                          )}
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-building-line"></i>
                              Şantiye
                            </span>
                            <span className="admin-info-value">
                              {waybill.site
                                ? `${waybill.site.project.name} / ${waybill.site.name}`
                                : 'Belirtilmedi'}
                            </span>
                          </div>
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-calendar-line"></i>
                              Tarih
                            </span>
                            <span className="admin-info-value">
                              {formatDate(waybill.scannedAt || waybill.createdAt)}
                            </span>
                          </div>
                        </div>
                        <div className="admin-card-footer">
                          <button
                            type="button"
                            className="admin-action-btn admin-btn-primary"
                            style={{ flex: 1 }}
                            onClick={() => openDetail(waybill)}
                          >
                            <i className="ri-eye-line me-1"></i>
                            Detay
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Logo Waybills Section */}
        <Row className="mt-2">
          <Col xs={12}>
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0">
                  <i className="ri-database-2-line me-2 text-info"></i>
                  Logo İrsaliyeleri
                </h5>
                <Button
                  color="light"
                  size="sm"
                  onClick={() => setShowLogoSection(!showLogoSection)}
                >
                  <i className={`ri-arrow-${showLogoSection ? 'up' : 'down'}-s-line`}></i>
                </Button>
              </CardHeader>
              <Collapse isOpen={showLogoSection}>
                <CardBody>
                  {/* Logo Filters */}
                  <div className="d-flex gap-2 mb-3">
                    <Input
                      type="select"
                      bsSize="sm"
                      value={logoStatusFilter}
                      onChange={(e) => setLogoStatusFilter(e.target.value as 'all' | 'matched' | 'pending')}
                    >
                      <option value="all">Tümü</option>
                      <option value="pending">Bekliyor</option>
                      <option value="matched">Eşleşti</option>
                    </Input>
                    <InputGroup size="sm">
                      <InputGroupText>
                        <i className="ri-search-line"></i>
                      </InputGroupText>
                      <Input
                        placeholder="Ara..."
                        value={logoSearch}
                        onChange={(e) => setLogoSearch(e.target.value)}
                      />
                    </InputGroup>
                  </div>

                  {logoLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="info" />
                      <p className="mt-2 text-muted">Logo'dan yükleniyor...</p>
                    </div>
                  ) : logoWaybills.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="ri-database-2-line display-4"></i>
                      <p className="mt-2">Logo'da irsaliye bulunamadı</p>
                    </div>
                  ) : (
                    <>
                      <div className="admin-card-list">
                        {logoWaybills
                          .filter((lw) => {
                            if (logoStatusFilter === 'matched') return lw.isMatched;
                            if (logoStatusFilter === 'pending') return !lw.isMatched;
                            return true;
                          })
                          .map((lw, idx) => (
                          <div
                            key={`${lw.ficheno}-${idx}`}
                            className="admin-card-item"
                            onClick={() => openLogoDetail(lw)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="admin-card-header">
                              <h6 className="admin-card-title">{lw.docode || lw.ficheno}</h6>
                              {lw.isMatched ? (
                                <Badge color="success">Eşleşti</Badge>
                              ) : (
                                <Badge color="warning">Bekliyor</Badge>
                              )}
                            </div>
                            <div className="admin-card-body">
                              <div className="admin-info-row">
                                <span className="admin-info-label">
                                  <i className="ri-store-2-line"></i>
                                  Tedarikçi
                                </span>
                                <span className="admin-info-value">{lw.clientName || '-'}</span>
                              </div>
                              <div className="admin-info-row">
                                <span className="admin-info-label">
                                  <i className="ri-calendar-line"></i>
                                  Tarih
                                </span>
                                <span className="admin-info-value">
                                  {new Date(lw.date).toLocaleDateString('tr-TR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {logoHasMore && (
                        <div className="text-center mt-3">
                          <Button
                            color="light"
                            size="sm"
                            onClick={loadMoreLogoWaybills}
                            disabled={logoLoadingMore}
                          >
                            {logoLoadingMore ? (
                              <>
                                <Spinner size="sm" className="me-1" />
                                Yükleniyor...
                              </>
                            ) : (
                              <>
                                <i className="ri-add-line me-1"></i>
                                Daha Fazla Yükle
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Collapse>
            </Card>
          </Col>
        </Row>

        {/* Detail Modal */}
        <WaybillDetailModal
          isOpen={showDetailModal}
          toggle={() => setShowDetailModal(false)}
          waybill={selectedWaybill}
          onUpdate={fetchWaybills}
        />

        {/* Logo Detail Modal */}
        <SideModal
          isOpen={showLogoDetailModal}
          toggle={() => setShowLogoDetailModal(false)}
          title="Logo İrsaliye Detayı"
          size="lg"
        >
          {selectedLogoWaybill && (
            <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
              <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}>
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <h4 className="mb-1" style={{ fontWeight: 600 }}>{selectedLogoWaybill.waybillNumber}</h4>
                    <p className="text-muted mb-0">Logo İrsaliye No</p>
                  </div>
                  {selectedLogoWaybill.isMatched ? (
                    <Badge color="success" className="px-2 py-1">Eşleşti</Badge>
                  ) : (
                    <Badge color="warning" className="px-2 py-1">Bekliyor</Badge>
                  )}
                </div>
              </div>
              <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }}>
                <div className="mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                  Logo Bilgileri
                </div>
                <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                  <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                    <span className="text-muted">Tedarikçi</span>
                    <span style={{ fontWeight: 500 }}>{selectedLogoWaybill.clientName || '-'}</span>
                  </div>
                  <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                    <span className="text-muted">Tarih</span>
                    <span style={{ fontWeight: 500 }}>
                      {new Date(selectedLogoWaybill.date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="d-flex align-items-center justify-content-between px-3 py-2">
                    <span className="text-muted">Fiş No</span>
                    <span style={{ fontWeight: 500 }}>{selectedLogoWaybill.ficheno}</span>
                  </div>
                </div>
              </div>
              <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }}>
                <div className="mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                  Malzemeler
                </div>
                {logoLinesLoading ? (
                  <div className="text-center py-3">
                    <Spinner size="sm" color="info" />
                  </div>
                ) : logoLines.length === 0 ? (
                  <div className="text-center py-3 text-muted small">Malzeme satırı bulunamadı</div>
                ) : (
                  <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                    {logoLines.map((line, idx) => (
                      <div key={idx} className="px-3 py-2" style={{ borderBottom: idx < logoLines.length - 1 ? '1px solid #e9ecef' : 'none' }}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <span style={{ fontWeight: 500 }}>{line.itemName}</span>
                            <div className="text-muted small">{line.itemCode}</div>
                          </div>
                          <div className="text-end">
                            <span style={{ fontWeight: 500 }}>{line.amount.toLocaleString('tr-TR')} {line.unitName || ''}</span>
                            {line.total > 0 && (
                              <div className="text-muted small">
                                {line.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {logoLines.some(l => l.total > 0) && (
                      <div className="px-3 py-2 d-flex justify-content-between" style={{ borderTop: '2px solid #dee2e6', background: '#e9ecef' }}>
                        <span style={{ fontWeight: 600 }}>Toplam</span>
                        <span style={{ fontWeight: 600 }}>
                          {logoLines.reduce((sum, l) => sum + l.total, 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {!selectedLogoWaybill.isMatched && (
                <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }}>
                  <div className="alert alert-warning mb-0">
                    <i className="ri-information-line me-2"></i>
                    Bu irsaliye henüz formen tarafından okutulmamış.
                  </div>
                </div>
              )}
            </div>
          )}
        </SideModal>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DESKTOP VIEW
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">İrsaliyeler</h4>
            <div className="page-title-right">
              <Button
                color="success"
                className="me-2"
                onClick={handleMatchAll}
                disabled={matching}
              >
                {matching ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="ri-refresh-line me-1"></i>
                )}
                Logo ile Eşleştir
              </Button>
              <Link to="/waybills/scan" className="btn btn-primary">
                <i className="ri-add-line me-1"></i>
                İrsaliye Ekle
              </Link>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={3}>
                  <h5 className="card-title mb-0">
                    <i className="ri-time-line me-2 text-warning"></i>
                    Eşleşme Bekleyen İrsaliyeler
                  </h5>
                </Col>
                <Col md={9}>
                  <Row className="g-2 align-items-center justify-content-end">
                    <Col md={3}>
                      <InputGroup size="sm">
                        <InputGroupText>
                          <i className="ri-search-line"></i>
                        </InputGroupText>
                        <Input
                          type="text"
                          placeholder="Ara..."
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md="auto">
                      <Input
                        type="select"
                        bsSize="sm"
                        value={statusFilterState}
                        onChange={(e) => setStatusFilterState(e.target.value)}
                        style={{ minWidth: '130px' }}
                      >
                        <option value="">Tüm Durumlar</option>
                        <option value="SCANNED">Okutuldu</option>
                        <option value="PENDING">Bekliyor</option>
                        <option value="NOT_ARRIVED">Gelmedi</option>
                        <option value="MANUAL">Manuel</option>
                      </Input>
                    </Col>
                    <Col md="auto">
                      <Input
                        type="select"
                        bsSize="sm"
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={{ minWidth: '180px' }}
                      >
                        <option value="">Tüm Şantiyeler</option>
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.project.name} / {site.name}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md="auto">
                      <InputGroup size="sm">
                        <Input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          style={{ maxWidth: '140px' }}
                        />
                        <InputGroupText>-</InputGroupText>
                        <Input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          style={{ maxWidth: '140px' }}
                        />
                      </InputGroup>
                    </Col>
                    {hasActiveFilters && (
                      <Col md="auto">
                        <Button
                          color="light"
                          size="sm"
                          onClick={clearFilters}
                          title="Filtreleri Temizle"
                        >
                          <i className="ri-close-line"></i>
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : waybills.length === 0 ? (
                <div className="text-center py-4">
                  <div className="mb-3">
                    <i className="ri-file-list-3-line text-muted" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5 className="text-muted">Henüz irsaliye kaydı bulunmuyor</h5>
                  <p className="text-muted mb-0">
                    QR kodu okutarak veya manuel olarak yeni irsaliye ekleyebilirsiniz.
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>İrsaliye No</th>
                        <th>Durum</th>
                        <th>Şantiye</th>
                        <th>Not</th>
                        <th>Okuyan</th>
                        <th>Okutma Tarihi</th>
                        <th style={{ width: '80px' }}>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waybills
                        .filter((w) => w.status !== 'MATCHED')
                        .map((waybill) => (
                          <tr key={waybill.id}>
                            <td>
                              <strong
                                className="waybill-number-link"
                                onClick={() => openDetail(waybill)}
                                style={{ cursor: 'pointer', color: '#405189' }}
                              >
                                {waybill.waybillNumber}
                              </strong>
                            </td>
                            <td>
                              <Badge color={statusColors[waybill.status]}>
                                {statusLabels[waybill.status]}
                              </Badge>
                            </td>
                            <td>
                              {waybill.site
                                ? `${waybill.site.project.name} / ${waybill.site.name}`
                                : '-'}
                            </td>
                            <td>
                              {waybill.notes ? (
                                <span title={waybill.notes}>
                                  {waybill.notes.length > 40
                                    ? waybill.notes.substring(0, 40) + '...'
                                    : waybill.notes}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {waybill.scannedBy
                                ? `${waybill.scannedBy.firstName} ${waybill.scannedBy.lastName}`
                                : '-'}
                            </td>
                            <td>
                              {waybill.scannedAt
                                ? formatDate(waybill.scannedAt)
                                : formatDate(waybill.createdAt)}
                            </td>
                            <td>
                              <Button
                                color="light"
                                size="sm"
                                onClick={() => openDetail(waybill)}
                              >
                                <i className="ri-eye-line"></i>
                              </Button>
                            </td>
                          </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Logo Waybills Section */}
      <Row className="mt-4">
        <Col lg={12}>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col>
                  <h5 className="card-title mb-0">
                    <i className="ri-database-2-line me-2 text-info"></i>
                    Logo İrsaliyeleri
                  </h5>
                </Col>
                <Col md="auto">
                  <div className="d-flex gap-2">
                    <Input
                      type="select"
                      bsSize="sm"
                      value={logoStatusFilter}
                      onChange={(e) => setLogoStatusFilter(e.target.value as 'all' | 'matched' | 'pending')}
                      style={{ width: '130px' }}
                    >
                      <option value="all">Tümü</option>
                      <option value="pending">Bekliyor</option>
                      <option value="matched">Eşleşti</option>
                    </Input>
                    <InputGroup size="sm" style={{ width: '200px' }}>
                      <InputGroupText>
                        <i className="ri-search-line"></i>
                      </InputGroupText>
                      <Input
                        placeholder="Ara..."
                        value={logoSearch}
                        onChange={(e) => setLogoSearch(e.target.value)}
                      />
                    </InputGroup>
                    <Button
                      color="light"
                      size="sm"
                      onClick={() => setShowLogoSection(!showLogoSection)}
                    >
                      <i className={`ri-arrow-${showLogoSection ? 'up' : 'down'}-s-line`}></i>
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardHeader>
            <Collapse isOpen={showLogoSection}>
              <CardBody>
                {logoLoading ? (
                  <div className="text-center py-4">
                    <Spinner color="info" />
                    <p className="mt-2 text-muted">Logo'dan yükleniyor...</p>
                  </div>
                ) : logoWaybills.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="ri-database-2-line display-4"></i>
                    <p className="mt-2">Logo'da irsaliye bulunamadı</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="table-hover table-striped align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>İrsaliye No</th>
                            <th>Tedarikçi</th>
                            <th>Tarih</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logoWaybills
                            .filter((lw) => {
                              if (logoStatusFilter === 'matched') return lw.isMatched;
                              if (logoStatusFilter === 'pending') return !lw.isMatched;
                              return true;
                            })
                            .map((lw, idx) => (
                            <tr
                              key={`${lw.ficheno}-${idx}`}
                              onClick={() => openLogoDetail(lw)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>
                                <span className="fw-medium text-primary">{lw.docode || lw.ficheno}</span>
                              </td>
                              <td>{lw.clientName || '-'}</td>
                              <td>
                                {new Date(lw.date).toLocaleDateString('tr-TR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                })}
                              </td>
                              <td>
                                {lw.isMatched ? (
                                  <Badge color="success">
                                    <i className="ri-check-line me-1"></i>
                                    Eşleşti
                                  </Badge>
                                ) : (
                                  <Badge color="warning">
                                    <i className="ri-time-line me-1"></i>
                                    Bekliyor
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {logoHasMore && (
                      <div className="text-center mt-3">
                        <Button
                          color="light"
                          size="sm"
                          onClick={loadMoreLogoWaybills}
                          disabled={logoLoadingMore}
                        >
                          {logoLoadingMore ? (
                            <>
                              <Spinner size="sm" className="me-1" />
                              Yükleniyor...
                            </>
                          ) : (
                            <>
                              <i className="ri-add-line me-1"></i>
                              Daha Fazla Yükle
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Collapse>
          </Card>
        </Col>
      </Row>

      {/* Detail Modal */}
      <WaybillDetailModal
        isOpen={showDetailModal}
        toggle={() => setShowDetailModal(false)}
        waybill={selectedWaybill}
        onUpdate={fetchWaybills}
      />

      {/* Logo Detail Modal */}
      <SideModal
        isOpen={showLogoDetailModal}
        toggle={() => setShowLogoDetailModal(false)}
        title="Logo İrsaliye Detayı"
        size="lg"
      >
        {selectedLogoWaybill && (
          <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
            {/* Header Card */}
            <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}>
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <h4 className="mb-1" style={{ fontWeight: 600 }}>
                    {selectedLogoWaybill.waybillNumber}
                  </h4>
                  <p className="text-muted mb-0">Logo İrsaliye No</p>
                </div>
                {selectedLogoWaybill.isMatched ? (
                  <Badge color="success" className="px-2 py-1">
                    <i className="ri-check-line me-1"></i>
                    Eşleşti
                  </Badge>
                ) : (
                  <Badge color="warning" className="px-2 py-1">
                    <i className="ri-time-line me-1"></i>
                    Bekliyor
                  </Badge>
                )}
              </div>
            </div>

            {/* Logo Bilgileri Card */}
            <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }}>
              <div className="mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                <i className="ri-database-2-line me-1"></i>
                Logo Bilgileri
              </div>
              <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                  <span className="text-muted">Tedarikçi</span>
                  <span style={{ fontWeight: 500 }}>{selectedLogoWaybill.clientName || '-'}</span>
                </div>
                <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                  <span className="text-muted">Tarih</span>
                  <span style={{ fontWeight: 500 }}>
                    {new Date(selectedLogoWaybill.date).toLocaleDateString('tr-TR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <div className="d-flex align-items-center justify-content-between px-3 py-2">
                  <span className="text-muted">Fiş No</span>
                  <span style={{ fontWeight: 500 }}>{selectedLogoWaybill.ficheno}</span>
                </div>
              </div>
            </div>

            {/* Malzeme Satırları Card */}
            <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }}>
              <div className="mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                <i className="ri-list-unordered me-1"></i>
                Malzemeler
              </div>
              {logoLinesLoading ? (
                <div className="text-center py-3">
                  <Spinner size="sm" color="info" />
                  <p className="text-muted small mt-2 mb-0">Malzemeler yükleniyor...</p>
                </div>
              ) : logoLines.length === 0 ? (
                <div className="text-center py-3 text-muted small">
                  <i className="ri-information-line me-1"></i>
                  Malzeme satırı bulunamadı
                </div>
              ) : (
                <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                  {logoLines.map((line, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-2"
                      style={{ borderBottom: idx < logoLines.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <span style={{ fontWeight: 500 }}>{line.itemName}</span>
                          <div className="text-muted small">{line.itemCode}</div>
                        </div>
                        <div className="text-end">
                          <span style={{ fontWeight: 500 }}>
                            {line.amount.toLocaleString('tr-TR')} {line.unitName || ''}
                          </span>
                          {line.total > 0 && (
                            <div className="text-muted small">
                              {line.price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺ × {line.amount.toLocaleString('tr-TR')} = {line.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {logoLines.some(l => l.total > 0) && (
                    <div className="px-3 py-2 d-flex justify-content-between" style={{ borderTop: '2px solid #dee2e6', background: '#e9ecef' }}>
                      <span style={{ fontWeight: 600 }}>Toplam</span>
                      <span style={{ fontWeight: 600 }}>
                        {logoLines.reduce((sum, l) => sum + l.total, 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Eşleşmişse Sistem Bilgileri */}
            {selectedLogoWaybill.isMatched && (
              <>
                {/* Forman Bilgileri Card */}
                <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }}>
                  <div className="mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                    <i className="ri-user-line me-1"></i>
                    Formen Bilgileri
                  </div>
                  {logoDetailLoading ? (
                    <div className="text-center py-4">
                      <Spinner size="sm" color="primary" />
                      <p className="text-muted small mt-2 mb-0">Yükleniyor...</p>
                    </div>
                  ) : matchedSystemWaybill ? (
                    <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                      <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                        <span className="text-muted">Şantiye</span>
                        <span style={{ fontWeight: 500 }}>
                          {matchedSystemWaybill.site
                            ? `${matchedSystemWaybill.site.project.name} / ${matchedSystemWaybill.site.name}`
                            : '-'}
                        </span>
                      </div>
                      <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                        <span className="text-muted">Okuyan</span>
                        <span style={{ fontWeight: 500 }}>
                          {matchedSystemWaybill.scannedBy
                            ? `${matchedSystemWaybill.scannedBy.firstName} ${matchedSystemWaybill.scannedBy.lastName}`
                            : '-'}
                        </span>
                      </div>
                      <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                        <span className="text-muted">Okutma Tarihi</span>
                        <span style={{ fontWeight: 500 }}>
                          {matchedSystemWaybill.scannedAt
                            ? formatDate(matchedSystemWaybill.scannedAt)
                            : '-'}
                        </span>
                      </div>
                      <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                        <span className="text-muted">Eşleşme Tarihi</span>
                        <span style={{ fontWeight: 500 }}>
                          {matchedSystemWaybill.gibMatchedAt
                            ? formatDate(matchedSystemWaybill.gibMatchedAt)
                            : '-'}
                        </span>
                      </div>
                      {matchedSystemWaybill.notes && (
                        <div className="px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                          <span className="text-muted d-block mb-1">Not</span>
                          <span style={{ fontWeight: 500 }}>{matchedSystemWaybill.notes}</span>
                        </div>
                      )}
                      {matchedSystemWaybill.photos && matchedSystemWaybill.photos.length > 0 && (
                        <div className="px-3 py-2">
                          <span className="text-muted d-block mb-2">Fotoğraflar ({matchedSystemWaybill.photos.length})</span>
                          <div className="d-flex flex-wrap gap-2">
                            {matchedSystemWaybill.photos.map((photo) => (
                              <div
                                key={photo.id}
                                className="rounded overflow-hidden"
                                style={{ width: '60px', height: '60px', cursor: 'pointer' }}
                                onClick={() => openAuthFile(photo.filePath)}
                              >
                                <AuthImage
                                  src={photo.filePath}
                                  alt={photo.fileName}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-3 text-muted">
                      <i className="ri-error-warning-line me-1"></i>
                      Sistem kaydı bulunamadı
                    </div>
                  )}
                </div>

                {/* QR Kod Bilgileri Card */}
                {matchedSystemWaybill?.qrCode && (
                  <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }}>
                    <div className="mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                      <i className="ri-qr-code-line me-1"></i>
                      QR Kod Bilgileri
                    </div>
                    <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                      {(() => {
                        try {
                          const qrData = JSON.parse(matchedSystemWaybill.qrCode);
                          const labels: Record<string, string> = {
                            vkntckn: 'Satıcı VKN/TCKN',
                            avkntckn: 'Alıcı VKN/TCKN',
                            senaryo: 'Senaryo',
                            tip: 'Tip',
                            tarih: 'Tarih',
                            no: 'Belge No',
                            ettn: 'ETTN',
                            sevktarihi: 'Sevk Tarihi',
                            sevkzamani: 'Sevk Zamanı',
                            tasiyicivkn: 'Taşıyıcı VKN',
                            plaka: 'Plaka',
                          };
                          const entries = Object.entries(qrData);
                          return entries.map(([key, value], idx) => (
                            <div
                              key={key}
                              className="d-flex align-items-center justify-content-between px-3 py-2"
                              style={{ borderBottom: idx < entries.length - 1 ? '1px solid #e9ecef' : 'none' }}
                            >
                              <span className="text-muted small">{labels[key] || key}</span>
                              <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{String(value)}</span>
                            </div>
                          ));
                        } catch {
                          if (matchedSystemWaybill.qrCode.includes('http')) {
                            return (
                              <div className="px-3 py-2">
                                <a
                                  href={matchedSystemWaybill.qrCode}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary small"
                                  style={{ wordBreak: 'break-all' }}
                                >
                                  {matchedSystemWaybill.qrCode}
                                </a>
                              </div>
                            );
                          }
                          return (
                            <div className="px-3 py-2">
                              <code style={{ wordBreak: 'break-all', fontSize: '0.8rem' }}>
                                {matchedSystemWaybill.qrCode}
                              </code>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Eşleşmemişse Bilgi */}
            {!selectedLogoWaybill.isMatched && (
              <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }}>
                <div className="alert alert-warning mb-0">
                  <i className="ri-information-line me-2"></i>
                  Bu irsaliye henüz formen tarafından okutulmamış.
                </div>
              </div>
            )}
          </div>
        )}
      </SideModal>
    </>
  );
};

export default WaybillList;
